import type { AccountRecord } from '@libs/data-access/entities'
import {
  Text,
  Stack,
  ArrowUpIcon,
  IconWrap,
  ArrowDownIcon,
  Modal,
  Button
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { createNextState } from '@reduxjs/toolkit'
import { useMemoSelector } from '@spa/redux/hooks'
import type { DebouncedFunc } from 'lodash-es'
import { mapKeys, sortBy } from 'lodash-es'
import React, { useCallback, useState } from 'react'
import type { FC } from 'react'
import { updateAccountReorder } from 'snap-redux/modules/accounts'
import { useActions } from 'snap-redux/utils'

import type { MembershipModel } from '../../redux/models/Membership'
import { contractsSelectors } from '../../redux/selectors'

import MembershipDraggableItem from './MembershipDraggableItem'

type Props = {
  account: AccountRecord
  teamId: number
  closeModal: DebouncedFunc<() => void>
  membershipsOfTeam: MembershipModel[]
}

const actionCreators = {
  updateAccountReorder
}
const MembershipSortForm: FC<Props> = ({
  teamId,
  account,
  closeModal,
  membershipsOfTeam
}) => {
  const { t, tCommon } = usePlanningFormTranslation()
  const [loading, setLoading] = useState(false)

  const contractsMappedByMembershipId = useMemoSelector(state => {
    const contractsMap = contractsSelectors.getCachedMap(state)
    return mapKeys(contractsMap, contract => contract.membership_id)
  })

  const actions = useActions(actionCreators)
  const membershipsSorted = sortBy(
    membershipsOfTeam,
    membership => membership.sort_index[teamId]
  )

  const [reorderedMemberships, setReorderedMemberships] =
    useState(membershipsSorted)

  const onSubmit = () => {
    setLoading(true)
    actions
      .updateAccountReorder(account.id, {
        memberships_attributes: reorderedMemberships.map(
          (membership, index) => ({
            id: membership.id,
            sort_index: {
              ...membership.sort_index,
              [teamId]: index
            }
          })
        )
      })
      .then(() => {
        setLoading(false)
        closeModal()
      })
      .catch(() => setLoading(false))
  }

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setReorderedMemberships((prevCards: any[]) => {
      return createNextState(prevCards, draftCards => {
        draftCards.splice(dragIndex, 1)
        draftCards.splice(hoverIndex, 0, prevCards[dragIndex])
      })
    })
  }, [])

  const orderCardsAlphabetically = ({ asc }: { asc: boolean }) => {
    const newOrderAlpha = [...reorderedMemberships].sort((a, b) => {
      const contractA = contractsMappedByMembershipId[a.id]
      const contractB = contractsMappedByMembershipId[b.id]
      return contractA.firstname.localeCompare(contractB.firstname)
    })

    const newOrder = asc ? newOrderAlpha : newOrderAlpha.reverse()
    return setReorderedMemberships(newOrder)
  }

  return (
    <>
      <Modal.Content>
        <div className='break-words'>
          <div className='mb-6'>
            <Text as='p' size='m' className='mb-6'>
              {t('membershipSort.description')}
            </Text>
            <Stack gap={3}>
              <Button
                variant='secondary'
                leftIcon={
                  <IconWrap
                    icon={<ArrowDownIcon />}
                    className='text-neutral-900'
                  />
                }
                onClick={() => orderCardsAlphabetically({ asc: true })}
              >
                {t('membershipSort.orderAsc')}
              </Button>
              <Button
                variant='secondary'
                leftIcon={
                  <IconWrap
                    icon={<ArrowUpIcon />}
                    className='text-neutral-900'
                  />
                }
                onClick={() => orderCardsAlphabetically({ asc: false })}
              >
                {t('membershipSort.orderDesc')}
              </Button>
            </Stack>
          </div>
          {reorderedMemberships.map((membership, i) => (
            <MembershipDraggableItem
              key={membership.id}
              index={i}
              membership={membership}
              moveCard={moveCard}
            />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Footer.Success
          fillWidth
          onClick={() => onSubmit()}
          loading={loading}
        >
          {tCommon('action.save')}
        </Modal.Footer.Success>
      </Modal.Footer>
    </>
  )
}

export default MembershipSortForm
